import { useState, type FormEvent } from "react";
import { Alert, Box, Button, IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth, LoginShell } from "@workspace/admin-ui";

export default function LoginPage() {
  const { login, resetPassword } = useAuth();
  const [mode, setMode] = useState<"signin" | "reset">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  }

  async function onResetSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await resetPassword(email.trim());
      setResetSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't send reset email");
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: "signin" | "reset") {
    setMode(next);
    setError(null);
    setResetSent(false);
  }

  return (
    <LoginShell
      appName="Chain Admin"
      subtitle={
        mode === "signin"
          ? "Sign in with your pharmacy chain admin account."
          : "Enter your email to receive a password reset link."
      }
    >
      {mode === "signin" ? (
        <Box component="form" onSubmit={onSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((v) => !v)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          {error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
          <Button type="submit" variant="contained" disabled={busy} fullWidth size="large">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => switchMode("reset")}
            sx={{ alignSelf: "center", color: "text.secondary", textTransform: "none" }}
          >
            Forgot password?
          </Button>
        </Box>
      ) : (
        <Box component="form" onSubmit={onResetSubmit} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            disabled={resetSent}
          />
          {error ? <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert> : null}
          {resetSent ? (
            <Alert severity="success" sx={{ py: 0.5 }}>
              If an account exists for that email, a reset link is on its way. Check your inbox.
            </Alert>
          ) : (
            <Button type="submit" variant="contained" disabled={busy} fullWidth size="large">
              {busy ? "Sending…" : "Send reset link"}
            </Button>
          )}
          <Button
            type="button"
            variant="text"
            size="small"
            onClick={() => switchMode("signin")}
            sx={{ alignSelf: "center", color: "text.secondary", textTransform: "none" }}
          >
            Back to sign in
          </Button>
        </Box>
      )}
    </LoginShell>
  );
}
